<template>
  <div class="agentic-view" v-if="kvdr && kvdr.agentic">
    <p class="subtitle-text">
      Should this role be augmented or scaled through AI? Auto-recommends based on fit, risk, and KVDR score.
    </p>
    
    <div class="metric-card">
      
      <!-- PAIN LEVEL -->
      <div class="form-group">
        <label>Pain Level</label>
        <div class="pill-group expanded">
          <button
            class="pill"
            :class="{active: kvdr.agentic.painLevel === 'high'}"
            @click="kvdr.agentic.painLevel = 'high'"
          >
            <span class="dot red"></span> High
          </button>

          <button
            class="pill"
            :class="{active: kvdr.agentic.painLevel === 'medium'}"
            @click="kvdr.agentic.painLevel = 'medium'"
          >
            <span class="dot yellow"></span> Medium
          </button>

          <button
            class="pill"
            :class="{active: kvdr.agentic.painLevel === 'low'}"
            @click="kvdr.agentic.painLevel = 'low'"
          >
            <span class="dot green"></span> Low
          </button>
        </div>
      </div>


      <!-- FREQUENCY -->
      <div class="form-group mt-16">
        <label>Frequency</label>

        <div class="pill-group expanded">

          <button
            class="pill"
            :class="{active: kvdr.agentic.frequency === 'daily'}"
            @click="kvdr.agentic.frequency = 'daily'"
          >
            <span class="icon">
              <!-- Calendar -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="17" rx="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </span>
            Daily
          </button>


          <button
            class="pill"
            :class="{active: kvdr.agentic.frequency === 'weekly'}"
            @click="kvdr.agentic.frequency = 'weekly'"
          >
            <span class="icon">
              <!-- Calendar with date -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="17" rx="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
                <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"></path>
              </svg>
            </span>
            Weekly
          </button>


          <button
            class="pill"
            :class="{active: kvdr.agentic.frequency === 'monthly'}"
            @click="kvdr.agentic.frequency = 'monthly'"
          >
            <span class="icon">
              <!-- Calendar grid -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="17" rx="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
                <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"></path>
              </svg>
            </span>
            Monthly
          </button>

        </div>
      </div>


      <!-- AI ADVANTAGE -->
      <div class="form-group mt-16">
        <label>AI Advantage</label>

        <div class="pill-group expanded">

          <button
            class="pill"
            :class="{active: kvdr.agentic.aiAdvantage === 'time'}"
            @click="kvdr.agentic.aiAdvantage = 'time'"
          >
            <span class="icon">
              <!-- Clock -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="9"></circle>
                <polyline points="12 7 12 12 15 14"></polyline>
              </svg>
            </span>
            Time
          </button>


          <button
            class="pill"
            :class="{active: kvdr.agentic.aiAdvantage === 'accuracy'}"
            @click="kvdr.agentic.aiAdvantage = 'accuracy'"
          >
            <span class="icon">
              <!-- Target -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="9"></circle>
                <circle cx="12" cy="12" r="5"></circle>
                <circle cx="12" cy="12" r="1"></circle>
              </svg>
            </span>
            Accuracy
          </button>


          <button
            class="pill"
            :class="{active: kvdr.agentic.aiAdvantage === 'scale'}"
            @click="kvdr.agentic.aiAdvantage = 'scale'"
          >
            <span class="icon">
              <!-- Trending up -->
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="3 17 9 11 13 15 21 7"></polyline>
                <polyline points="14 7 21 7 21 14"></polyline>
              </svg>
            </span>
            Scale
          </button>

        </div>
      </div>

    </div>


    <!-- SLIDERS & TEXT -->
    <div class="metric-card">

      <div class="form-group">
        <label>
          <span class="icon">
            <!-- Shield -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 3L4 7v5c0 5 3.5 8 8 9 4.5-1 8-4 8-9V7l-8-4z"></path>
            </svg>
          </span>
          Risk tolerance for automation
          <span class="score-badge">{{ kvdr.agentic.riskTolerance }}/5</span>
        </label>

        <input
          type="range"
          min="1"
          max="5"
          v-model.number="kvdr.agentic.riskTolerance"
          class="slider"
          :style="{'--fill-percent': ((kvdr.agentic.riskTolerance - 1) / 4) * 100 + '%'}"
        >

        <div class="slider-labels">
          <span>Zero tolerance</span>
          <span>Comfortable with errors</span>
        </div>
      </div>


      <div class="form-group mt-16">
        <label>
          Exception handling load (%)
          <span class="score-badge">{{ kvdr.agentic.exceptionLoadPct }}%</span>
        </label>

        <input
          type="range"
          min="0"
          max="100"
          v-model.number="kvdr.agentic.exceptionLoadPct"
          class="slider"
          :style="{'--fill-percent': kvdr.agentic.exceptionLoadPct + '%'}"
        >

        <p class="hint mt-4">
          % of work that's edge cases requiring human judgment.
        </p>
      </div>


      <div class="form-group mt-16">
        <label>Value upside from augmentation</label>

        <textarea
          v-model="kvdr.agentic.valueUpside"
          class="text-input"
          placeholder="What new outcomes become possible with AI assistance?"
        ></textarea>
      </div>

    </div>


    <!-- DYNAMIC RECOMMENDATION BOX -->
    <div :class="['alignment-box', recommendationClass]">

      <div class="ab-header">

        <span class="ab-title">
          <span class="icon">
            <!-- Bot -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="10" rx="2"></rect>
              <circle cx="12" cy="5" r="2"></circle>
              <path d="M12 7v4"></path>
              <line x1="8" y1="16" x2="8" y2="16"></line>
              <line x1="16" y1="16" x2="16" y2="16"></line>
            </svg>
          </span>
          RECOMMENDATION
        </span>

        <span class="ab-badge">
          Fit {{ agentFitScore }}/27
        </span>

      </div>


      <div class="ab-logic">
        <strong class="ab-logic-val">
          {{ agenticRecommendation.action }}
        </strong>
      </div>


      <div class="ab-metrics mt-12">

        <div class="ab-metric">
          <span class="ab-m-label">COST OF HUMAN-ONLY</span>
          <span class="ab-m-val">
            {{ currentCurrencySymbol }}{{ formatNumber(totalEmploymentCost) }}
          </span>
        </div>

        <div class="ab-metric">
          <span class="ab-m-label">EST. AUTOMATION SAVING</span>
          <span class="ab-m-val">
            {{ currentCurrencySymbol }}{{ formatNumber(estimatedSaving) }}/yr
          </span>
        </div>

      </div>


      <div class="ab-logic mt-12">
        <span class="ab-logic-sub">
          <span class="icon">
            <!-- Message -->
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 11.5a7.5 7.5 0 0 1-7.5 7.5H8l-4 2v-4.5A7.5 7.5 0 1 1 20 11.5z"></path>
              <path d="M8 11h.01M12 11h.01M16 11h.01"></path>
            </svg>
          </span>
          {{ agenticRecommendation.subtext }}
        </span>
      </div>

    </div>


    <!-- AGENTIZE TOGGLE -->
    <div class="toggle-card">

      <div class="toggle-info">

        <span class="icon text-gray">
          <!-- Bot -->
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="10" rx="2"></rect>
            <circle cx="12" cy="5" r="2"></circle>
            <path d="M12 7v4"></path>
            <line x1="8" y1="16" x2="8" y2="16"></line>
            <line x1="16" y1="16" x2="16" y2="16"></line>
          </svg>
        </span>

        <div>
          <h4>Agentize this Role</h4>
          <p>Attach an AI agent to handle this role's tasks</p>
        </div>

      </div>

      <label class="switch">
        <input
          type="checkbox"
          v-model="kvdr.agentic.agentizeRole"
        >
        <span class="slider-toggle round"></span>
      </label>

    </div>

  </div>
</template>

<script>
export default {
  name: 'AgenticLayer',
  props: {
    kvdr: { type: Object, required: true },
    currentCurrencySymbol: { type: String, required: true },
    formatNumber: { type: Function, required: true },
    totalEmploymentCost: { type: Number, required: true },
    agentFitScore: { type: Number, required: true },
    agenticRecommendation: { type: Object, required: true },
    estimatedSaving: { type: Number, required: true }
  },
  computed: {
    recommendationClass() {
      const act = this.agenticRecommendation.action;
      if (act === 'Agentize partial') return 'band-aligned'; // Green vibe
      if (act === 'Hybridize' || act === 'Augment') return 'band-under'; // Yellow/Orange vibe
      return 'band-over'; // Grey/Red vibe for Do not automate
    }
  }
}
</script>

<style scoped>
/* Standardized Spacing */
.agentic-view { display: flex; flex-direction: column; gap: 20px; padding-bottom: 24px; }
.subtitle-text { font-size: 13px; color: #64748b; margin: 0 0 4px 0; }
.mt-16 { margin-top: 16px; }
.mt-12 { margin-top: 12px; }
.mt-4 { margin-top: 4px; }

/* Uniform card padding */
.metric-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 24px; display: flex; flex-direction: column; }
.form-group { display: flex; flex-direction: column; gap: 6px; }
label { font-size: 11px; font-weight: 600; color: #64748b; display: flex; align-items: center; gap: 6px; margin-bottom: 2px; }
.icon { font-size: 14px; }
.hint { font-size: 11px; color: #94a3b8; margin: 0; line-height: 1.4; }
.text-input { width: 100%; padding: 12px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; height: 70px; resize: vertical; outline: none; font-family: inherit; transition: 0.2s; } 
.text-input:focus { border-color: #F59E0B; box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1); }

/* PILLS */
.pill-group { display: flex; gap: 8px; }
.pill-group.expanded button { flex: 1; justify-content: center; }
.pill { padding: 10px 16px; border-radius: 8px; border: 1px solid #cbd5e1; background: #fff; font-size: 12px; color: #475569; cursor: pointer; font-weight: 600; transition: all 0.2s; display: flex; align-items: center; gap: 8px; }
.pill:hover { border-color: #94a3b8; background: #f8fafc; }
.pill.active { background: #FFFBEB; border-color: #F59E0B; color: #d97706; }
.dot { width: 8px; height: 8px; border-radius: 50%; }
.dot.red { background: #EF4444; } .dot.yellow { background: #F59E0B; } .dot.green { background: #10B981; }

/* FIXED: Slider CSS variables support */
.slider { width: 100%; appearance: none; height: 6px; border-radius: 4px; outline: none; background: linear-gradient(to right, #F59E0B 0%, #F59E0B var(--fill-percent, 0%), #e2e8f0 var(--fill-percent, 0%), #e2e8f0 100%); margin: 6px 0; }
.slider::-webkit-slider-thumb { appearance: none; width: 16px; height: 16px; border-radius: 50%; background: #F59E0B; cursor: pointer; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
.slider-labels { display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; margin-top: 4px; font-weight: 500; }
.score-badge { margin-left: auto; color: #0f172a; font-weight: 700; font-size: 12px; }

/* 🌟 DYNAMIC ALIGNMENT BOX CSS 🌟 */
.alignment-box { border-radius: 8px; padding: 24px; display: flex; flex-direction: column; border: 1px solid transparent; transition: all 0.3s ease; }
.band-aligned { background: #ECFDF5; border-color: #6EE7B7; } /* Green */
.band-aligned .ab-title, .band-aligned .ab-badge, .band-aligned .ab-logic-val { color: #059669; }
.band-aligned .ab-m-val { color: #059669; }

.band-under { background: #FFFBEB; border-color: #FCD34D; } /* Yellow */
.band-under .ab-title, .band-under .ab-badge, .band-under .ab-logic-val { color: #D97706; }
.band-under .ab-m-val { color: #D97706; }

.band-over { background: #f1f5f9; border-color: #cbd5e1; } /* Grey */
.band-over .ab-title, .band-over .ab-badge, .band-over .ab-logic-val { color: #475569; }
.band-over .ab-m-val { color: #475569; }

.ab-header { display: flex; justify-content: space-between; align-items: center; padding-bottom: 12px; }
.ab-title { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px; }
.ab-badge { font-size: 12px; font-weight: 700; }
.ab-logic-val { font-size: 20px; font-weight: 800; }
.ab-logic-sub { font-size: 12px; color: #475569; font-weight: 500; display: flex; align-items: center; gap: 6px;}

.ab-metrics { display: flex; gap: 40px; }
.ab-metric { display: flex; flex-direction: column; gap: 2px; }
.ab-m-label { font-size: 10px; font-weight: 700; color: #94a3b8; text-transform: uppercase; }
.ab-m-val { font-size: 14px; font-weight: 800; }

/* TOGGLE CARD */
.toggle-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 24px; display: flex; justify-content: space-between; align-items: center; }
.toggle-info { display: flex; align-items: center; gap: 12px; }
.toggle-info h4 { margin: 0 0 2px 0; font-size: 14px; color: #0f172a; font-weight: 700; }
.toggle-info p { margin: 0; font-size: 12px; color: #64748b; }
.text-gray { font-size: 20px; }

.switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider-toggle { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #cbd5e1; transition: .4s; }
.slider-toggle:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background-color: white; transition: .4s; }
input:checked + .slider-toggle { background-color: #F59E0B; }
input:focus + .slider-toggle { box-shadow: 0 0 1px #F59E0B; }
input:checked + .slider-toggle:before { transform: translateX(20px); }
.slider-toggle.round { border-radius: 24px; }
.slider-toggle.round:before { border-radius: 50%; }
.metric-card .icon,
.pill .icon,
.ab-title .icon,
.ab-logic-sub .icon,
.toggle-info .icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    flex-shrink: 0;
}

.metric-card .icon svg,
.pill .icon svg,
.ab-title .icon svg,
.ab-logic-sub .icon svg,
.toggle-info .icon svg {
    width: 16px;
    height: 16px;
    display: block;
    stroke: currentColor;
}
</style>